import { ImageIcon } from 'lucide-react'

import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { AriaAttributes, ComponentProps, ComponentType, ReactNode } from 'react'

const mediaPlaceholderSizes = {
  xs: 'size-12',
  sm: 'size-14',
  md: 'size-20',
  preview: 'h-32 w-full',
  fill: 'size-full',
  custom: '',
}

const mediaPlaceholderIconSizes = {
  xs: 'size-4',
  sm: 'size-4',
  md: 'size-5',
  preview: 'size-6',
  fill: 'size-5',
  custom: 'size-5',
}

export type MediaPlaceholderSize = keyof typeof mediaPlaceholderSizes

export type MediaPlaceholderProps = ComponentProps<'div'> & {
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: AriaAttributes['aria-hidden'] }>
  label?: ReactNode
  size?: MediaPlaceholderSize
  iconClassName?: string
}

function MediaPlaceholder({
  icon: Icon = ImageIcon,
  label,
  size = 'custom',
  className,
  iconClassName,
  ...props
}: MediaPlaceholderProps) {
  return (
    <div
      data-slot="media-placeholder"
      data-size={size}
      className={cn(
        'grid shrink-0 place-items-center overflow-hidden rounded-xl border border-border/50 bg-muted/30 text-muted-foreground',
        mediaPlaceholderSizes[size] ?? mediaPlaceholderSizes.custom,
        className
      )}
      {...props}
    >
      <div className="grid justify-items-center gap-2 px-3 text-center">
        <Icon
          className={cn(
            mediaPlaceholderIconSizes[size] ?? mediaPlaceholderIconSizes.custom,
            iconClassName
          )}
          aria-hidden="true"
        />
        {label ? <Typography variant="caption">{label}</Typography> : null}
      </div>
    </div>
  )
}

export { MediaPlaceholder }
